import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { handleError } from "../../utils/functions";
import axiosInstance from "../../config/axios.config";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

const RatingFilter = ({ value, filters, setFilters }) => {
  const renderStars = (ratingValue) => {
    return [...Array(5)].map((_, index) => (
      <span
        key={index}
        className={`text-xs fw-medium ${index < ratingValue ? " text-warning-600" : "text-gray-400"} d-flex`}
      >
        <i className={"ph-fill ph-star"} />
      </span>
    ));
  };

  return (
    <div className="flex-align gap-8 position-relative mb-20">
      <label className="position-absolute w-100 h-100 cursor-pointer" htmlFor={`rating${value}`}>
        {" "}
      </label>
      <div className="common-check common-radio mb-0">
        <input
          className="form-check-input"
          type="radio"
          name="flexRadioDefault"
          id={`rating${value}`}
          checked={filters.rating === value}
          onChange={() => setFilters((prev) => ({ ...prev, rating: value }))}
        />
      </div>
      <div
        className="progress w-100 bg-gray-100 rounded-pill h-8"
        role="progressbar"
        aria-label="Basic example"
        aria-valuenow={70}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="progress-bar bg-main-600 rounded-pill" style={{ width: `${value * 20}%` }} />
      </div>
      <div className="flex-align gap-4">{renderStars(value)}</div>
    </div>
  );
};

const CategoryFilter = ({ category, accessor = "category", preserve = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const categoryId = searchParams.get(accessor);

  const updateSearchParams = (newValue) => {
    if (preserve) {
      setSearchParams(
        (prev) => {
          prev.set(accessor, newValue);
          return prev;
        },
        {
          replace: true,
          preventScrollReset: true,
        }
      );
    } else {
      setSearchParams({ [accessor]: newValue });
    }
  };

  return (
    <li className="mb-24">
      <div className="common-check common-radio mb-0">
        <input
          className="form-check-input"
          type="radio"
          name={accessor}
          id={`category${category.value}`}
          checked={categoryId === category.value}
          onChange={() => updateSearchParams(category.value)}
        />
        &nbsp;
        <span className="cursor-pointer" onClick={() => updateSearchParams(category.value)}>
          {category.label}
        </span>
      </div>
    </li>
  );
};

const ProductFilters = ({ filters, setFilters }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get("category");

  let [active, setActive] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategory, setsubCategory] = useState([]);

  let sidebarController = () => {
    setActive(!active);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/category/active");
        if (!data.error) {
          setCategories(data.data);
        }
      } catch (error) {
        handleError(error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!categoryId) return;
    const getData = async () => {
      try {
        const { data } = await axiosInstance.get(`/subcategory/active/${categoryId}`);
        if (!data.error) {
          setsubCategory(data.data);
        }
      } catch (error) {
        handleError(error);
      }
    };
    getData();
  }, [categoryId]);

  const clearFilters = () => {
    setFilters({
      name: "",
      rating: 0,
      maxPrice: "",
      minPrice: "",
    });
    setSearchParams();
  };

  return (
    <div className="col-lg-3">
      <div className={`shop-sidebar ${active && "active"}`}>
        <button
          onClick={sidebarController}
          type="button"
          className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-600"
        >
          <i className="ph ph-x" />
        </button>
        <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
          <div className="d-flex justify-content-between align-items-center pb-24 mb-24 border-bottom border-gray-100">
            <h6 className="text-xl mb-0">{t("productSearch")}</h6>
            <button className="" onClick={clearFilters}>
              X
            </button>
          </div>
          <div className="searchInput">
            <input
              type="search"
              placeholder={t("searchProducts")}
              value={filters.name}
              onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
            />
            <Icon icon="ic:baseline-search" />
          </div>
        </div>

        <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
          <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">{t("filterByRating")}</h6>
          {[5, 4, 3, 2, 1].map((item) => (
            <RatingFilter key={item} value={item} filters={filters} setFilters={setFilters} />
          ))}
        </div>
        <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
          <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">{t("productCategory")}</h6>
          <ul className="scrollDiv overflow-y-auto scroll-sm">
            {categories.map((category) => (
              <CategoryFilter category={category} key={category.value} />
            ))}
          </ul>
        </div>
        {categoryId && subCategory.length > 0 && (
          <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
            <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">Sub Category</h6>
            <ul className="scrollDiv overflow-y-auto scroll-sm">
              {subCategory.map((subcategory) => (
                <CategoryFilter
                  category={subcategory}
                  key={subcategory.value}
                  accessor="subcategory"
                  preserve={true}
                />
              ))}
            </ul>
          </div>
        )}
        <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
          <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">{t("filterByPrice")}</h6>
          <div className="custom--range">
            <div className="">
              <input
                type="number"
                placeholder={t("minPrice")}
                value={filters.minPrice}
                className="mb-6"
                onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
              />
              <input
                type="number"
                placeholder={t("maxPrice")}
                value={filters.maxPrice}
                onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductFilters;
